/**
 * LocalStorage utility service.
 * Handles browser storage for connections, tabs, query history, and preferences.
 * Inspired by DBeaver's workspace persistence.
 */

import { Connection, SqlTab, QueryHistoryItem, Preferences } from '../types';

const STORAGE_KEYS = {
  TOKEN: 'sql_client_token',
  CONNECTIONS: 'sql_client_connections',
  TABS: 'sql_client_tabs',
  QUERY_HISTORY: 'sql_client_query_history',
  PREFERENCES: 'sql_client_preferences',
  ACTIVE_TAB: 'sql_client_active_tab',
};

// Token Management
export const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

export const setToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};

// Connection Management
export const getConnections = (): Connection[] => {
  const data = localStorage.getItem(STORAGE_KEYS.CONNECTIONS);
  return data ? JSON.parse(data) : [];
};

export const saveConnections = (connections: Connection[]): void => {
  localStorage.setItem(STORAGE_KEYS.CONNECTIONS, JSON.stringify(connections));
};

export const addConnection = (connection: Connection): void => {
  const connections = getConnections();
  connections.push(connection);
  saveConnections(connections);
};

export const updateConnection = (connection: Connection): void => {
  const connections = getConnections();
  const index = connections.findIndex(c => c.id === connection.id);
  if (index !== -1) {
    connections[index] = connection;
    saveConnections(connections);
  }
};

export const deleteConnection = (id: string): void => {
  const connections = getConnections();
  const filtered = connections.filter(c => c.id !== id);
  saveConnections(filtered);
};

// Tab Management
export const getTabs = (): SqlTab[] => {
  const data = localStorage.getItem(STORAGE_KEYS.TABS);
  return data ? JSON.parse(data) : [];
};

export const saveTabs = (tabs: SqlTab[]): void => {
  localStorage.setItem(STORAGE_KEYS.TABS, JSON.stringify(tabs));
};

export const getActiveTabId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACTIVE_TAB);
};

export const setActiveTabId = (tabId: string): void => {
  localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, tabId);
};

// Query History Management
export const getQueryHistory = (): QueryHistoryItem[] => {
  const data = localStorage.getItem(STORAGE_KEYS.QUERY_HISTORY);
  return data ? JSON.parse(data) : [];
};

export const saveQueryHistory = (history: QueryHistoryItem[]): void => {
  // Keep only last 100 items
  const limited = history.slice(-100);
  localStorage.setItem(STORAGE_KEYS.QUERY_HISTORY, JSON.stringify(limited));
};

export const addQueryToHistory = (item: QueryHistoryItem): void => {
  const history = getQueryHistory();
  history.push(item);
  saveQueryHistory(history);
};

export const clearQueryHistory = (): void => {
  localStorage.setItem(STORAGE_KEYS.QUERY_HISTORY, JSON.stringify([]));
};

// Preferences Management
export const getPreferences = (): Preferences => {
  const data = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
  return data ? JSON.parse(data) : getDefaultPreferences();
};

export const savePreferences = (preferences: Preferences): void => {
  localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(preferences));
};

const getDefaultPreferences = (): Preferences => ({
  autoCommit: true,
  dateFormat: 'YYYY-MM-DD HH:mm:ss',
  pageSize: 100,
  theme: 'light',
});

// Re-export encryption utilities
export { encryptPassword, decryptPassword } from './encryption';

// Clear all storage
export const clearAllStorage = (): void => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

